import React from 'react';
import { Link, scrollSpy } from 'react-scroll';
import { useHistory } from 'react-router';
import { Container } from '@mui/system';
import { navBarList } from 'utils/Constant';
import { getQueryParams, scrollToTop } from 'utils/Utils';
import SearchContainer from 'components/wrapper/SearchContainer';

/* ── design tokens (shared with DashboardLayout) ── */
const T = {
  bg: '#F6F5F0',
  nav: '#FFFFFF',
  soft: '#F0EEE6',
  line: '#E5E2D8',
  ink: '#232821',
  mut: '#6E766C',
  acc: '#2E9E7A',
  shadow: '0 1px 2px rgba(35,40,33,.04), 0 4px 16px rgba(35,40,33,.06)',
};

/* ── search pill custom styles ── */
const searchStyles = {
  option: (provided, state) => ({
    ...provided,
    fontSize: 13,
    textAlign: 'left',
    cursor: 'pointer',
    color: state.isSelected ? T.acc : T.ink,
    backgroundColor: state.isSelected ? `${T.acc}18` : T.nav,
  }),
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    height: 34,
    paddingLeft: 8,
    borderRadius: 999,
    border: `1px solid ${T.line}`,
    backgroundColor: T.soft,
    boxShadow: 'none',
    '&:hover': { borderColor: T.acc },
    cursor: 'pointer',
    margin: 0,
  }),
  dropdownIndicator: (base) => ({ ...base, display: 'none' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  valueContainer: (base) => ({ ...base, padding: '0 4px' }),
  placeholder: (base) => ({
    ...base,
    color: T.mut,
    fontSize: 12.5,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
  }),
  input: (base) => ({
    ...base,
    fontSize: 12.5,
    margin: 0,
    padding: 0,
    color: T.ink,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: T.shadow,
    border: `1px solid ${T.line}`,
    overflow: 'hidden',
    zIndex: 100,
  }),
};

/* ════════════════════════════════════════
   DashboardNavbar
   – Top bar (logo · search · company · toggle)
   – Tab bar  (pill links with react-scroll)
════════════════════════════════════════ */
const DashboardNavbar = () => {
  const history = useHistory();
  const Params = getQueryParams(history.location.search);
  const [active, setActive] = React.useState('company_essential');
  const [onClicked, setOnClicked] = React.useState(false);

  /* Initialize scrollSpy and Debounce active tab updates */
  React.useEffect(() => {
    scrollSpy.update();
    const t = setTimeout(() => setOnClicked(false), 1000);
    return () => clearTimeout(t);
  }, [active]);

  const onTabClick = (id) => { setOnClicked(true); setActive(id); };
  const onTabScroll = (id) => { if (!onClicked) setActive(id); };

  const goHome = () => { history.push('/'); scrollToTop(); };

  return (
    <React.Fragment>
      {/* ══════════════════════
          TOP BAR
      ══════════════════════ */}
      <div style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.nav,
        flexShrink: 0,
        zIndex: 10,
      }}>
        <Container maxWidth="xl" style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          paddingTop: 12,
          paddingBottom: 12,
        }}>
          {/* Logo */}
          <div
            className="dml-logo"
            onClick={goHome}
            style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: T.ink,
              cursor: 'pointer',
              userSelect: 'none',
              flexShrink: 0,
            }}
          >
            finalyze<span style={{ color: T.acc }}>.</span>
          </div>

          {/* Search pill */}
          <div style={{ flex: '0 0 auto', width: 340 }}>
            <SearchContainer
              placeHolder={
                <span style={{ color: T.mut, fontSize: 12.5 }}>
                  Search another company
                </span>
              }
              customStyles={searchStyles}
              icon={null}
            />
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Company name + symbol */}
          {Params.company__name && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{
                fontFamily: "'Bricolage Grotesque', sans-serif",
                fontSize: 18,
                fontWeight: 800,
                color: T.ink,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {Params.company__name}
              </div>
              <div style={{ fontSize: 10.5, color: T.mut }}>
                NSE: {Params.company__symbol}
              </div>
            </div>
          )}

          {/* Theme toggle */}
          <button
            className="dml-theme"
            title="Toggle light / dark"
            aria-label="Toggle theme"
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              border: `1px solid ${T.line}`,
              background: T.nav,
              color: T.ink,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none',
              flexShrink: 0,
            }}
          >
            ☾
          </button>
        </Container>
      </div>

      {/* ══════════════════════
          TAB NAV
      ══════════════════════ */}
      <div
        style={{
          borderBottom: `1px solid ${T.line}`,
          background: T.nav,
          flexShrink: 0,
          zIndex: 9,
        }}
      >
        <Container maxWidth="xl" className="dml-scroll" style={{
          display: 'flex',
          gap: 4,
          paddingTop: 10,
          paddingBottom: 10,
          overflowX: 'auto',
        }}>
          {navBarList.map((item, index) => {
            const isActive = active === item.id;
            return (
              <Link
                key={index}
                style={{ flexShrink: 0 }}
                spy={true}
                to={item.id}
                smooth={true}
                offset={-20}
                containerId="dml-content-scroll"
                onClick={() => onTabClick(item.id)}
                onSetActive={() => onTabScroll(item.id)}
              >
                <button
                  className="dml-tab"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 999,
                    border: `1px solid ${T.ink}`,
                    background: isActive ? T.ink : 'transparent',
                    color: isActive ? T.bg : T.mut,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                    outline: 'none',
                    flexShrink: 0,
                  }}
                >
                  {item.name}
                </button>
              </Link>
            );
          })}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardNavbar;